import config from 'player/config';

import { TOKEN_FETCH_ERROR } from '../playback/config/status-codes';
import type { StreamModel } from './stream';

class TokenV2 {
    data: TokenPayload | Record<string, never> = {};

    request: Promise<string | null> | null = null;

    isFetching: boolean = false;

    private async fetchToken({
        assetId,
        vendor,
        drmEncryption,
        paywalled,
    }: {
        assetId: number;
        vendor: string;
        drmEncryption: boolean;
        paywalled: boolean;
    }) {
        this.isFetching = true;

        // Wait for identity/session to be ready before hitting the token API.
        if (config.api.identityReady && paywalled) {
            await config.api.identityReady();
        }

        const url = new URL(`${config.api.tokenV2Url}${vendor}/${assetId}`);
        if (drmEncryption) {
            url.searchParams.set('drmEncryption', 'true');
        }

        const response = await fetch(url, { credentials: 'include' });

        this.isFetching = false;

        if (!response.ok) {
            return Promise.reject(TOKEN_FETCH_ERROR);
        }

        this.data = await response.json();
        return this.getValue();
    }

    private async refetch(stream: StreamModel) {
        if (this.request && this.isFetching) {
            return this.request;
        }

        if (stream.isSecure()) {
            this.request = this.fetchToken({
                assetId: stream.getId(),
                vendor: stream.getVendor(),
                drmEncryption: stream.isDrmProtected(),
                paywalled: stream.hasAccess(),
            });
            return this.request;
        }
        this.request = Promise.resolve(null);
    }

    public async fetch(stream: StreamModel) {
        if (!this.isValid()) {
            await this.refetch(stream);
        }

        return this.request;
    }

    public async fetchDrmToken(stream: StreamModel) {
        if (!this.isDrmTokenValid()) {
            await this.refetch(stream);
        }

        return this.request;
    }

    private getValue() {
        const { signedHlsUrl } = this.data;
        if (!signedHlsUrl) {
            return null;
        }
        return new URL(signedHlsUrl).searchParams.get('hdnea');
    }

    public async getSignedUrl(stream: StreamModel) {
        await this.fetch(stream);
        if (!this.isValid()) {
            return null;
        }
        return this.data.signedHlsUrl || null;
    }

    public isValid() {
        // on load expiry is 0 therefore it will always return false
        return Date.now() < this.data.expiry * 1000;
    }

    public async getDrmToken(stream: StreamModel) {
        try {
            await this.fetchDrmToken(stream);
        } catch (error) {
            if (Number.isInteger(error)) {
                return null;
            }
            throw error;
        }

        if (this.data.drmToken) {
            const token = this.data.drmToken.value;

            // mark token as expired as it can be used only once
            this.data.drmToken.expiry = 0;

            return token;
        }

        return null;
    }

    public isDrmTokenValid() {
        if (this.data.drmToken) {
            // add a 10s expiration margin to mitigate in-flight token expiration
            return Date.now() < (this.data.drmToken.expiry - 10) * 1000;
        }

        return false;
    }
}

export default TokenV2;

export type TokenPayload = {
    expiry: number;
    hasAccess: boolean;
    loggedIn: boolean;
    signedHlsUrl: string;
    value: string;
    drmToken?: {
        value: string;
        expiry: number;
    };
};
